import { projectsData } from './projectsData';
import { parseVoteData } from './voteResponse';
import { parseChildrenData } from './childrenResponse';
import { parseGouterData } from './gouterResponse';
import { parseExemptionsData, parseImportResult, parseManualHoursData } from './hoursResponse';
import { isActiveBiweekly } from './biweekly';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UserRole = 'tuteur' | 'chef_projet' | 'bureau';

export interface PlanningUser {
    id: string;
    name: string;
    login: string;
    password: string;
    role: UserRole;
    projectIds: number[];
    /** Jeton de session signé remis par le script à la connexion (exigé par les actions de vote). */
    token?: string;
}

export type DayOfWeek = 'Lundi' | 'Mardi' | 'Mercredi' | 'Jeudi' | 'Vendredi' | 'Samedi';

export interface TimeSlot {
    id: string;
    projectId: number;
    day: DayOfWeek;
    startTime: string;
    endTime: string;
}

export type BookingStatus = 'prevu' | 'confirme' | 'absent' | 'annule';

export interface Booking {
    id: string;
    slotId: string;
    userId: string;
    userName?: string;
    weekKey: string;
    status: BookingStatus;
    validatedBy?: string;
    validatedAt?: string;
}

export const SPECIAL_EVENTS = [
    { id: 'simonu', label: 'SimONU' },
    { id: 'entretiens', label: 'Entretiens d’Excellence' },
    { id: 'jedc', label: 'Journée Egalité des Chances' },
    { id: 'olympiades', label: 'Olympiades' }
];

export interface EventAttendance {
    userId: string;
    eventId: string;
    present: boolean;
}

// L'URL directe n'est plus utilisée depuis le front-end pour éviter les problèmes CORS sur Vercel
// export const GOOGLE_APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbyyMsp23ni8GWsIVXrj8xuBpjLLNgqRsxlsyEBWmQt4xAlKDBHzLqXtKW5vAdzMESMeXg/exec";

export async function authenticateUser(loginId: string, password: string): Promise<PlanningUser | null> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/login`, {
            method: 'POST',
            body: JSON.stringify({ login: loginId, password }),
            headers: { 'Content-Type': 'application/json' }
        });
        
        if (!response.ok) {
            console.error("Erreur HTTP:", response.status);
            return null;
        }

        const data = await response.json();
        
        if (data.success && data.user) {
            return data.user as PlanningUser;
        } else {
            console.warn("Erreur d'authentification:", data.message);
            return null;
        }
    } catch (error) {
        console.error("Erreur lors de la requête vers l'API de login:", error);
        return null;
    }
}

// ─── Time Slots ───────────────────────────────────────────────────────────────

export const timeSlots: TimeSlot[] = projectsData.flatMap((project) => {
    switch (project.id) {
        case 4: // ST GAB : 18h00-19h30 Lundi Mardi Jeudi Vendredi
            return ['Lundi', 'Mardi', 'Jeudi', 'Vendredi'].map(day => ({
                id: `slot-4-${day.toLowerCase().substring(0,3)}`,
                projectId: 4, day: day as DayOfWeek, startTime: '18:00', endTime: '19:30'
            }));
        case 6: // Izzo : 16h30-18h30 Jeudi
            return [{ id: 'slot-6-jeu', projectId: 6, day: 'Jeudi', startTime: '16:30', endTime: '18:30' }];
        case 2: // ACSE : 14h00-17h30 Samedi
            return [{ id: 'slot-2-sam', projectId: 2, day: 'Samedi', startTime: '14:00', endTime: '17:30' }];
        case 5: // Apprentis d’Auteuil : 16h00-18h30 Mardi Jeudi
            return ['Mardi', 'Jeudi'].map(day => ({
                id: `slot-5-${day.toLowerCase().substring(0,3)}`,
                projectId: 5, day: day as DayOfWeek, startTime: '16:00', endTime: '18:30'
            }));
        case 8: // Arthur Rimbaud : 15h30-18h00 Jeudi
            return [{ id: 'slot-8-jeu', projectId: 8, day: 'Jeudi', startTime: '15:30', endTime: '18:00' }];
        case 7: // Jules Ferry : 14h00-16h30 Mercredi (Bi-hebdomadaire)
            return [{ id: 'slot-7-mer', projectId: 7, day: 'Mercredi', startTime: '14:00', endTime: '16:30' }];
        case 1: // Sup d'OM : Mardi, Jeudi, Samedi (plus de séance le mercredi)
            return [
                { id: 'slot-1-mar', projectId: 1, day: 'Mardi', startTime: '18:00', endTime: '19:30' },
                { id: 'slot-1-jeu', projectId: 1, day: 'Jeudi', startTime: '18:00', endTime: '19:30' },
                { id: 'slot-1-sam', projectId: 1, day: 'Samedi', startTime: '10:00', endTime: '12:30' }
            ];
        case 9: // Roy d’Espagne : 16h00-18h30 Jeudi
            return [{ id: 'slot-9-jeu', projectId: 9, day: 'Jeudi', startTime: '16:00', endTime: '18:30' }];
        case 3: // MASSA 13 : Mercredi 18h00-20h00
            return [{ id: 'slot-3-mer', projectId: 3, day: 'Mercredi', startTime: '18:00', endTime: '20:00' }];
        default:
            return [];
    }
});

const JULES_FERRY_ANCHOR_WEEK = '2026-W45';

export function isSlotActiveThisWeek(slotId: string, weekKey: string): boolean {
    if (slotId.startsWith('slot-7-')) { // Jules Ferry
        // Une semaine sur deux, calée sur la séance du 2 au 8 novembre 2026 (2026-W45)
        return isActiveBiweekly(weekKey, JULES_FERRY_ANCHOR_WEEK);
    }
    return true;
}

// ─── Slot utilities ───────────────────────────────────────────────────────────

export function getSlotDuration(slot: TimeSlot): number {
    const [sh, sm] = slot.startTime.split(':').map(Number);
    const [eh, em] = slot.endTime.split(':').map(Number);
    return (eh * 60 + em - sh * 60 - sm) / 60;
}

export const DAY_ORDER: DayOfWeek[] = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];


// ─── Week utilities ───────────────────────────────────────────────────────────

export function getWeekKey(date: Date): string {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

export function getWeekStartDate(weekKey: string): Date {
    const [year, week] = weekKey.split('-W').map(Number);
    const jan4 = new Date(year, 0, 4);
    const jan4Day = jan4.getDay() || 7;
    const start = new Date(jan4);
    start.setDate(jan4.getDate() - jan4Day + 1 + (week - 1) * 7);
    return start;
}

export function formatWeekLabel(weekKey: string): string {
    const start = getWeekStartDate(weekKey);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('fr-FR', opts)} – ${end.toLocaleDateString('fr-FR', { ...opts, year: 'numeric' })}`;
}

export function navigateWeek(weekKey: string, direction: 'prev' | 'next'): string {
    const start = getWeekStartDate(weekKey);
    start.setDate(start.getDate() + (direction === 'next' ? 7 : -7));
    return getWeekKey(start);
}

// ─── API Sync Utilities ───────────────────────────────────────────────────────

export async function fetchPlanningData(): Promise<{ bookings: Booking[], unavailableWeeks: string[], eventAttendance: EventAttendance[] } | null> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`);
        if (response.ok) {
            const data = await response.json();
            if (data.success) {
                const rawBookings: Booking[] = data.bookings || [];
                
                // Déduplication côté client
                const bookingMap = new Map<string, Booking>();
                for (const b of rawBookings) {
                    const uniqueKey = `${b.userId}_${b.slotId}_${b.weekKey}`;
                    const existing = bookingMap.get(uniqueKey);
                    if (!existing || b.status === 'confirme' || b.status === 'absent') {
                        bookingMap.set(uniqueKey, b);
                    }
                }

                return {
                    bookings: Array.from(bookingMap.values()),
                    unavailableWeeks: data.unavailableWeeks || [],
                    eventAttendance: data.eventAttendance || []
                };
            }
        }
        return null;
    } catch (error) {
        console.error("Error fetching planning data:", error);
        return null;
    }
}

export async function syncBookingApi(booking: Booking): Promise<boolean> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ action: 'syncBooking', booking }),
            headers: { 'Content-Type': 'application/json' }
        });
        return response.ok;
    } catch (error) {
        console.error("Error syncing booking:", error);
        return false;
    }
}

export async function deleteBookingApi(booking: Booking): Promise<boolean> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ 
                action: 'deleteBooking', 
                id: booking.id,
                userId: booking.userId,
                slotId: booking.slotId,
                weekKey: booking.weekKey
            }),
            headers: { 'Content-Type': 'application/json' }
        });
        return response.ok;
    } catch (error) {
        console.error("Error deleting booking:", error);
        return false;
    }
}

export async function toggleWeekUnavailableApi(userId: string, weekKey: string, isUnavailable: boolean): Promise<boolean> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ action: 'toggleWeekUnavailable', userId, weekKey, isUnavailable }),
            headers: { 'Content-Type': 'application/json' }
        });
        return response.ok;
    } catch (error) {
        console.error("Error toggling week unavailability:", error);
        return false;
    }
}

export async function syncEventAttendanceApi(attendance: EventAttendance): Promise<boolean> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ action: 'syncEventAttendance', attendance }),
            headers: { 'Content-Type': 'application/json' }
        });
        return response.ok;
    } catch (error) {
        console.error("Error syncing event attendance:", error);
        return false;
    }
}

export async function updateUserProjectApi(userId: string, projectIds: number[]): Promise<{ success: boolean; message: string }> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ action: 'updateUserProject', userId, projectIds }),
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Erreur réseau avec le serveur' };
        }
        const data = await response.json();
        return {
            success: !!data.success,
            message: data.message || (data.success ? 'Projet mis à jour avec succès' : 'Échec de la mise à jour du projet')
        };
    } catch (error) {
        console.error("Error updating user project:", error);
        return { success: false, message: "Erreur serveur lors de l'enregistrement du projet" };
    }
}

export async function changePasswordApi(userId: string, oldPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ action: 'changePassword', userId, oldPassword, newPassword }),
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Erreur réseau avec le serveur' };
        }
        const data = await response.json();
        return {
            success: !!data.success,
            message: data.message || (data.success ? 'Mot de passe modifié avec succès' : 'Échec de la modification')
        };
    } catch (error) {
        console.error("Error changing password:", error);
        return { success: false, message: "Erreur serveur lors du changement de mot de passe" };
    }
}

export async function requestResetCodeApi(loginId: string): Promise<{ success: boolean; message: string; maskedEmail?: string }> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ action: 'requestResetCode', login: loginId }),
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Erreur réseau avec le serveur' };
        }
        const data = await response.json();
        return {
            success: !!data.success,
            message: data.message || (data.success ? 'Code envoyé avec succès' : 'Échec de l’envoi'),
            maskedEmail: data.maskedEmail
        };
    } catch (error) {
        console.error("Error requesting reset code:", error);
        return { success: false, message: "Erreur serveur lors de la demande de code" };
    }
}

export async function confirmResetPasswordApi(loginId: string, code: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify({ action: 'confirmResetPassword', login: loginId, code, newPassword }),
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Erreur réseau avec le serveur' };
        }
        const data = await response.json();
        return {
            success: !!data.success,
            message: data.message || (data.success ? 'Mot de passe réinitialisé avec succès' : 'Échec de la réinitialisation')
        };
    } catch (error) {
        console.error("Error confirming reset password:", error);
        return { success: false, message: "Erreur serveur lors de la confirmation du mot de passe" };
    }
}



// ─── Vote du tuteur du mois ───────────────────────────────────────────────────

export interface VoteCandidate {
    id: string;
    name: string;
}

export interface VoteData {
    monthKey: string;
    isOpen: boolean;
    opensOn: string;
    closesOn: string;
    nextOpensOn: string;
    candidates: VoteCandidate[];
    myVote: string | null;
    winner: { userId: string; name: string } | null;
    /** Présent uniquement pour le Bureau. */
    bureau?: {
        counts: { userId: string; name: string; count: number }[];
        /** Mois du vote que le Bureau prépare : le vote en cours s'il est ouvert, sinon le prochain. */
        selectionMonthKey: string;
        members: { id: string; name: string; selected: boolean }[];
    };
}

export interface ChildrenSummaryChild {
    id: string;
    name: string;
    sessions: number;
    present: number;
    rate: number | null;
}

export interface ChildrenData {
    success: true;
    isBureau: boolean;
    /** Projets auxquels le compte a accès (le Bureau les voit tous). */
    projects: number[];
    projectId: number | null;
    /** Semaine affichée, au format 2026-W44. */
    weekKey: string;
    /** Séance (créneau) demandée, ou null tant qu'aucune n'est choisie. */
    slotId: string | null;
    children: { id: string; name: string }[];
    /** Appel déjà fait pour la séance `slotId` de la semaine `weekKey`, ou null s'il n'a pas eu lieu. */
    attendance: { slotId: string; weekKey: string; presentIds: string[] } | null;
    /** Créneaux du projet dont l'appel est déjà fait cette semaine. */
    doneSlots: string[];
    summary: {
        schoolYear: string;
        childCount: number;
        sessionCount: number;
        rate: number | null;
        children: ChildrenSummaryChild[];
    } | null;
    /** Vue d'ensemble de tous les projets, réservée au Bureau. */
    overview: { projectId: number; childCount: number; sessionCount: number; rate: number | null }[] | null;
}

const VOTE_UNAVAILABLE = "Le vote n'est pas disponible pour le moment.";

async function postPlanningAction(body: Record<string, unknown>, unavailableMessage: string): Promise<{ success: boolean; message: string; code?: string; data?: Record<string, unknown> }> {
    try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_URL}/api/planning`, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            return { success: false, message: 'Erreur réseau avec le serveur' };
        }
        const data = await response.json();
        return {
            success: !!data.success,
            code: typeof data.code === 'string' ? data.code : undefined,
            // Un script Google pas encore redéployé traite ces actions comme une connexion et répond
            // "Identifiants incorrects" : on n'affiche jamais ce texte.
            message: data.success ? (data.message || '') : (!data.message || data.message === 'Action inconnue' || data.message === 'Identifiants incorrects' ? unavailableMessage : data.message),
            data
        };
    } catch (error) {
        console.error('Error calling planning API:', error);
        return { success: false, message: 'Erreur réseau avec le serveur' };
    }
}

const postVoteAction = (body: Record<string, unknown>) => postPlanningAction(body, VOTE_UNAVAILABLE);

export async function fetchVoteData(requesterId: string, token: string): Promise<{ ok: true; data: VoteData } | { ok: false; message: string; code?: string }> {
    const result = await postVoteAction({ action: 'getVoteData', requesterId, token });
    if (!result.success) return { ok: false, message: result.message, code: result.code };
    const data = parseVoteData(result.data);
    return data ? { ok: true, data } : { ok: false, message: VOTE_UNAVAILABLE };
}

export async function castVoteApi(voterId: string, candidateId: string, token: string): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postVoteAction({ action: 'castVote', voterId, candidateId, token });
    return { success, message, code };
}

export async function withdrawVoteApi(voterId: string, token: string): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postVoteAction({ action: 'withdrawVote', voterId, token });
    return { success, message, code };
}

export async function setVoteCandidateApi(requesterId: string, userId: string, selected: boolean, token: string): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postVoteAction({ action: 'setVoteCandidate', requesterId, userId, selected, token });
    return { success, message, code };
}

export async function publishWinnerApi(requesterId: string, userId: string | null, token: string): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postVoteAction({ action: 'publishWinner', requesterId, userId, token });
    return { success, message, code };
}

const CHILDREN_UNAVAILABLE = "La présence des enfants n'est pas disponible pour le moment.";

const postChildrenAction = (body: Record<string, unknown>) => postPlanningAction(body, CHILDREN_UNAVAILABLE);

export async function fetchChildrenData(
    requesterId: string,
    token: string,
    projectId: number | null,
    weekKey: string,
    slotId: string | null
): Promise<{ ok: true; data: ChildrenData } | { ok: false; message: string; code?: string }> {
    const result = await postChildrenAction({ action: 'getChildrenData', requesterId, token, projectId, weekKey, slotId });
    if (!result.success) return { ok: false, message: result.message, code: result.code };
    const data = parseChildrenData(result.data);
    return data ? { ok: true, data } : { ok: false, message: CHILDREN_UNAVAILABLE };
}

export async function addChildrenApi(requesterId: string, token: string, projectId: number, names: string[]): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postChildrenAction({ action: 'addChildren', requesterId, token, projectId, names });
    return { success, message, code };
}

export async function removeChildApi(requesterId: string, token: string, projectId: number, childId: string): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postChildrenAction({ action: 'removeChild', requesterId, token, projectId, childId });
    return { success, message, code };
}

export async function saveChildAttendanceApi(requesterId: string, token: string, projectId: number, slotId: string, weekKey: string, presentIds: string[]): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postChildrenAction({ action: 'saveChildAttendance', requesterId, token, projectId, slotId, weekKey, presentIds });
    return { success, message, code };
}

// ─── Goûters des projets ──────────────────────────────────────────────────────

export interface GouterSlot {
    /** Heure de début du créneau d'une heure, de 9 à 18. */
    hour: number;
    users: { userId: string; name: string }[];
}

export interface GouterItem {
    id: string;
    item: string;
    userId: string;
    name: string;
    canRemove: boolean;
}

export interface Gouter {
    id: string;
    projectId: number;
    /** Jour du goûter, AAAA-MM-JJ. */
    date: string;
    note: string;
    isPast: boolean;
    /** Vrai pour le Bureau et pour un chef de ce projet. */
    canManage: boolean;
    slots: GouterSlot[];
    items: GouterItem[];
}

export interface GouterData {
    success: true;
    isBureau: boolean;
    schoolYear: string;
    /** Projets dont le compte peut ajouter ou annuler des goûters. */
    manageableProjects: number[];
    gouters: Gouter[];
}

const GOUTER_UNAVAILABLE = "Les goûters ne sont pas disponibles pour le moment.";

const postGouterAction = (body: Record<string, unknown>) => postPlanningAction(body, GOUTER_UNAVAILABLE);

type GouterWriteResult = Promise<{ success: boolean; message: string; code?: string }>;

export async function fetchGouters(requesterId: string, token: string): Promise<{ ok: true; data: GouterData } | { ok: false; message: string; code?: string }> {
    const result = await postGouterAction({ action: 'getGouters', requesterId, token });
    if (!result.success) return { ok: false, message: result.message, code: result.code };
    const data = parseGouterData(result.data);
    return data ? { ok: true, data } : { ok: false, message: GOUTER_UNAVAILABLE };
}

export async function addGoutersApi(
    requesterId: string,
    token: string,
    projectId: number,
    dates: string[],
    note: string
): Promise<{ success: boolean; message: string; code?: string; added: number; ignored: string[]; reasons: string[] }> {
    const { success, message, code, data } = await postGouterAction({ action: 'addGouters', requesterId, token, projectId, dates, note });
    const ignored = Array.isArray(data?.ignored) ? (data.ignored as unknown[]).map(String) : [];
    const reasons = Array.isArray(data?.reasons) ? (data.reasons as unknown[]).map(String) : [];
    return { success, message, code, added: typeof data?.added === 'number' ? data.added : 0, ignored, reasons };
}

export async function cancelGouterApi(requesterId: string, token: string, gouterId: string): GouterWriteResult {
    const { success, message, code } = await postGouterAction({ action: 'cancelGouter', requesterId, token, gouterId });
    return { success, message, code };
}

export async function moveGouterApi(requesterId: string, token: string, gouterId: string, date: string): GouterWriteResult {
    const { success, message, code } = await postGouterAction({ action: 'moveGouter', requesterId, token, gouterId, date });
    return { success, message, code };
}

export async function setGouterSlotApi(requesterId: string, token: string, gouterId: string, hour: number, joined: boolean, userId?: string): GouterWriteResult {
    const { success, message, code } = await postGouterAction({ action: 'setGouterSlot', requesterId, token, gouterId, hour, joined, userId });
    return { success, message, code };
}

export async function addGouterItemApi(requesterId: string, token: string, gouterId: string, item: string): GouterWriteResult {
    const { success, message, code } = await postGouterAction({ action: 'addGouterItem', requesterId, token, gouterId, item });
    return { success, message, code };
}

export async function removeGouterItemApi(requesterId: string, token: string, itemId: string): GouterWriteResult {
    const { success, message, code } = await postGouterAction({ action: 'removeGouterItem', requesterId, token, itemId });
    return { success, message, code };
}

// ─── Heures manuelles et membres renouvelants (enregistrés dans le Sheet) ─────

export interface ManualHourEntry {
    id: string;
    userId: string;
    userName: string;
    hours: number;
    reason: string;
    grantedBy: string;
    /** Absent des attributions importées de l'ancien stockage dans le navigateur. */
    grantedById?: string;
    createdAt: string;
    /** Absent pour la mission générale. */
    projectId?: number;
}

export interface QuotaExemption {
    userId: string;
    userName: string;
    grantedBy: string;
    grantedAt: string;
    year: string; // e.g. "2024-2025"
    note?: string;
}

export interface ManualHoursData {
    isBureau: boolean;
    /** Projets pour lesquels le compte peut attribuer des heures (tous pour le Bureau). */
    manageableProjects: number[];
    manualHours: ManualHourEntry[];
}

export interface ExemptionsData {
    exemptions: QuotaExemption[];
}

const HOURS_UNAVAILABLE = "Les heures manuelles ne sont pas disponibles pour le moment.";

const postHoursAction = (body: Record<string, unknown>) => postPlanningAction(body, HOURS_UNAVAILABLE);

type HoursWriteResult = Promise<{ success: boolean; message: string; code?: string }>;
type HoursImportResult = Promise<{ ok: true; imported: number; ignored: number } | { ok: false; message: string; code?: string }>;

export async function fetchManualHours(requesterId: string, token: string): Promise<{ ok: true; data: ManualHoursData } | { ok: false; message: string; code?: string }> {
    const result = await postHoursAction({ action: 'getManualHours', requesterId, token });
    if (!result.success) return { ok: false, message: result.message, code: result.code };
    const data = parseManualHoursData(result.data);
    return data ? { ok: true, data } : { ok: false, message: HOURS_UNAVAILABLE };
}

export async function addManualHoursApi(
    requesterId: string,
    token: string,
    userId: string,
    userName: string,
    hours: number,
    reason: string,
    projectId?: number
): HoursWriteResult {
    const { success, message, code } = await postHoursAction({ action: 'addManualHours', requesterId, token, userId, userName, hours, reason, projectId });
    return { success, message, code };
}

export async function deleteManualHoursApi(requesterId: string, token: string, id: string): HoursWriteResult {
    const { success, message, code } = await postHoursAction({ action: 'deleteManualHours', requesterId, token, id });
    return { success, message, code };
}

export async function importManualHoursApi(requesterId: string, token: string, entries: unknown[]): HoursImportResult {
    const result = await postHoursAction({ action: 'importManualHours', requesterId, token, entries });
    if (!result.success) return { ok: false, message: result.message, code: result.code };
    const parsed = parseImportResult(result.data);
    return parsed ? { ok: true, ...parsed } : { ok: false, message: HOURS_UNAVAILABLE };
}

export async function fetchExemptions(requesterId: string, token: string): Promise<{ ok: true; data: ExemptionsData } | { ok: false; message: string; code?: string }> {
    const result = await postHoursAction({ action: 'getExemptions', requesterId, token });
    if (!result.success) return { ok: false, message: result.message, code: result.code };
    const data = parseExemptionsData(result.data);
    return data ? { ok: true, data } : { ok: false, message: HOURS_UNAVAILABLE };
}

export async function setExemptionApi(
    requesterId: string,
    token: string,
    userId: string,
    userName: string,
    exempt: boolean,
    note?: string,
    year?: string
): HoursWriteResult {
    const { success, message, code } = await postHoursAction({ action: 'setExemption', requesterId, token, userId, userName, exempt, note, year });
    return { success, message, code };
}

export async function importExemptionsApi(requesterId: string, token: string, entries: unknown[]): HoursImportResult {
    const result = await postHoursAction({ action: 'importExemptions', requesterId, token, entries });
    if (!result.success) return { ok: false, message: result.message, code: result.code };
    const parsed = parseImportResult(result.data);
    return parsed ? { ok: true, ...parsed } : { ok: false, message: HOURS_UNAVAILABLE };
}

// ─── Membres placés sur un créneau par un responsable ─────────────────────────

const MEMBER_BOOKING_UNAVAILABLE = "Le placement de membres sur un créneau n'est pas disponible pour le moment.";

const postMemberBookingAction = (body: Record<string, unknown>) => postPlanningAction(body, MEMBER_BOOKING_UNAVAILABLE);

export async function addMemberBookingsApi(
    requesterId: string,
    token: string,
    slotId: string,
    weekKey: string,
    userIds: string[]
): Promise<{ success: boolean; message: string; code?: string; added: number; updated: number; already: number }> {
    const { success, message, code, data } = await postMemberBookingAction({ action: 'addMemberBookings', requesterId, token, slotId, weekKey, userIds });
    const count = (value: unknown) => (typeof value === 'number' ? value : 0);
    return { success, message, code, added: count(data?.added), updated: count(data?.updated), already: count(data?.already) };
}

export async function removeMemberBookingApi(requesterId: string, token: string, bookingId: string): Promise<{ success: boolean; message: string; code?: string }> {
    const { success, message, code } = await postMemberBookingAction({ action: 'removeMemberBooking', requesterId, token, bookingId });
    return { success, message, code };
}
